// ---- URL hash sharing ---------------------------------------------------
// Encode the whole bracket state into a compact, URL-safe token so picks can be
// shared by link. Pure functions (no DOM) so they're node-testable; the glue to
// window.location lives in main.jsx.
//
// Wire format (JSON, then url-safe base64):
//   { g: [[firstIdx, secondIdx, thirdIdx] × 12],  // local team indices 0..3, -1 if unset
//     t: [groupIdx, ...],                          // thirdsAdvancing, in selection order
//     w: { matchId: globalTeamIdx } }              // knockout winners, index into ALL_TEAMS
// Teams are stored as indices (never names) so the payload is short and ASCII.

import { GROUPS, GROUP_KEYS, ALL_TEAMS } from "./data.js";

const HASH_PREFIX = "#s=";

// --- url-safe base64 over ASCII JSON ---
function toBase64Url(str) {
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function fromBase64Url(token) {
  let b64 = token.replace(/-/g, "+").replace(/_/g, "/");
  while (b64.length % 4) b64 += "=";
  return atob(b64);
}

export function encodeState(state) {
  const groupResults = state?.groupResults || {};
  const thirdsAdvancing = state?.thirdsAdvancing || [];
  const winners = state?.winners || {};

  const g = GROUP_KEYS.map(gk => {
    const res = groupResults[gk] || {};
    return [
      GROUPS[gk].indexOf(res.first),
      GROUPS[gk].indexOf(res.second),
      GROUPS[gk].indexOf(res.third)
    ];
  });

  const t = thirdsAdvancing.map(letter => GROUP_KEYS.indexOf(letter)).filter(i => i >= 0);

  const w = {};
  for (const matchId of Object.keys(winners)) {
    const idx = ALL_TEAMS.indexOf(winners[matchId]);
    if (idx >= 0) w[matchId] = idx;
  }

  return toBase64Url(JSON.stringify({ g, t, w }));
}

export function decodeState(token) {
  if (typeof token !== "string" || token.length === 0) return null;
  let obj;
  try {
    obj = JSON.parse(fromBase64Url(token));
  } catch {
    return null;
  }
  if (!obj || typeof obj !== "object" || !Array.isArray(obj.g) || !Array.isArray(obj.t) || typeof obj.w !== "object" || obj.w === null) {
    return null;
  }

  const groupResults = {};
  GROUP_KEYS.forEach((gk, i) => {
    const entry = obj.g[i];
    if (!Array.isArray(entry)) return;
    const teams = GROUPS[gk];
    const res = {};
    const [f, s, th] = entry;
    if (Number.isInteger(f) && teams[f]) res.first = teams[f];
    if (Number.isInteger(s) && teams[s]) res.second = teams[s];
    if (Number.isInteger(th) && teams[th]) res.third = teams[th];
    if (Object.keys(res).length) groupResults[gk] = res;
  });

  const thirdsAdvancing = obj.t
    .map(i => GROUP_KEYS[i])
    .filter(letter => letter !== undefined);

  const winners = {};
  for (const matchId of Object.keys(obj.w)) {
    const team = ALL_TEAMS[obj.w[matchId]];
    if (team) winners[matchId] = team;
  }

  return { groupResults, thirdsAdvancing, winners };
}

// --- location.hash glue (still pure: string in, string/state out) ---
export function buildHash(state) {
  return HASH_PREFIX + encodeState(state);
}

export function parseHash(hash) {
  if (typeof hash !== "string" || !hash.startsWith(HASH_PREFIX)) return null;
  const token = hash.slice(HASH_PREFIX.length);
  if (!token) return null;
  return decodeState(token);
}
